import { useState, useEffect, useCallback, useMemo } from 'react';
import { PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { getPrioritizeSection, getEffectivePrioritizeSection } from '../utils/taskUtils';
import { DND_DELAY_MOBILE, DND_MOBILE_TOLERANCE_PX } from '../utils/dndConstants';

function useIsTouchDevice() {
  return useMemo(
    () =>
      typeof window !== 'undefined' &&
      (navigator.maxTouchPoints > 0 || 'ontouchstart' in window),
    []
  );
}

/**
 * Hook for handling drag-and-drop operations in the Prioritize view
 * @param {Object} params - Configuration object
 * @param {Array} params.tasks - All tasks
 * @param {string} params.currentFileId - Current file ID
 * @param {Function} params.updateTaskOrders - Function to batch update task orders
 * @param {Function} params.fetchTasks - Function to refresh tasks on error
 * @param {Function} params.getTodayDate - Function to get today's date in YYYY-MM-DD format
 * @param {Function} params.getLaterDate - Function to get later date in YYYY-MM-DD format
 * @param {Set} params.visiblePrioritizeSections - Visible section keys (when work-in-progress visible, wip tasks use that section)
 * @param {Function} params.updateTaskStatus - Function to update task status (for moving to/from work-in-progress)
 */
export function usePrioritizeDragDrop({
  tasks,
  currentFileId,
  updateTaskOrders,
  fetchTasks,
  getTodayDate,
  getLaterDate,
  visiblePrioritizeSections = null,
  updateTaskStatus = null
}) {
  const getSection = useCallback((task) => getEffectivePrioritizeSection(task, visiblePrioritizeSections), [visiblePrioritizeSections]);
  const [dropPosition, setDropPosition] = useState(null); // 'before' or 'after'
  const [mouseY, setMouseY] = useState(null);
  const [dragOverTaskId, setDragOverTaskId] = useState(null);
  const [dragOverSection, setDragOverSection] = useState(null); // Section being dragged over (header or task)
  const [dragOverSectionHeader, setDragOverSectionHeader] = useState(null); // Section id only when pointer is over the header

  // Custom collision detection that excludes the active (dragged) item.
  // Uses 2D distance so that when sections are side-by-side (desktop), only droppables
  // in the same column as the pointer are considered (horizontal position matters).
  const collisionDetection = useCallback((args) => {
    const { active, droppableRects, pointerCoordinates } = args;
    
    let closest = null;
    let minDistance = Infinity;
    
    for (const [id, rect] of droppableRects.entries()) {
      if (id === active.id) continue; // Skip the dragged item
      
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const distance = Math.hypot(
        pointerCoordinates.x - centerX,
        pointerCoordinates.y - centerY
      );
      
      if (distance < minDistance) {
        minDistance = distance;
        closest = { id, rect };
      }
    }
    
    return closest ? [{ id: closest.id }] : [];
  }, []);

  const isTouchDevice = useIsTouchDevice();
  // Desktop: PointerSensor (30px) so mouse drag works. Touch: only TouchSensor (long-press + tolerance) so scrolling doesn't start a drag.
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 5 },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: DND_DELAY_MOBILE, tolerance: DND_MOBILE_TOLERANCE_PX },
  });
  const sensors = useSensors(
    ...(isTouchDevice ? [touchSensor] : [pointerSensor, touchSensor])
  );

  // Track pointer/touch position during drag (mouseY used for drop position calculation)
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMouseY(e.clientY);
    };
    const handleTouchMove = (e) => {
      if (e.touches && e.touches.length > 0) {
        setMouseY(e.touches[0].clientY);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  // Calculate drop position based on mouse Y position
  const calculateDropPosition = useCallback((over, active, mouseY) => {
    if (!over || !mouseY) return 'after';
    
    const overElement = document.getElementById(over.id);
    if (!overElement) return 'after';
    
    const rect = overElement.getBoundingClientRect();
    const centerY = rect.top + rect.height / 2;
    
    return mouseY < centerY ? 'before' : 'after';
  }, []);

  const handleDragOver = useCallback((event) => {
    const { active, over } = event;
    
    if (!over) {
      setDragOverTaskId(null);
      setDragOverSection(null);
      setDragOverSectionHeader(null);
      setDropPosition(null);
      return;
    }

    // Check if dropping on a section header (section IDs start with "section-")
    if (over.id && over.id.startsWith('section-')) {
      const section = over.id.replace('section-', '');
      // Check if section has tasks - if so, show drop indicator below header
      const sectionTasks = tasks.filter(t => {
        if (t.file_id !== currentFileId) return false;
        return getSection(t) === section;
      });
      setDragOverSection(section);
      setDragOverSectionHeader(section);
      setDragOverTaskId(null);
      setDropPosition(sectionTasks.length > 0 ? 'after' : null);
      return;
    }
    
    // Also check if the element has data-section attribute (e.g. section header)
    const overElement = document.getElementById(over.id);
    if (overElement && overElement.dataset.section) {
      setDragOverSection(overElement.dataset.section);
      setDragOverSectionHeader(overElement.dataset.section);
      setDragOverTaskId(null);
      setDropPosition(null);
      return;
    }

    // Dropping on a task
    const dropTargetClientId = over.id;
    const draggedTaskClientId = active.id;

    if (draggedTaskClientId === dropTargetClientId) {
      setDragOverTaskId(null);
      setDropPosition(null);
      return;
    }

    const dropTargetTask = tasks.find(t => t._reactKey === dropTargetClientId && t.file_id === currentFileId);
    const targetSection = dropTargetTask ? getSection(dropTargetTask) : null;
    setDragOverSection(targetSection);
    setDragOverSectionHeader(null); // Over a task, not the header
    setDragOverTaskId(dropTargetClientId);
    
    const calculatedPosition = calculateDropPosition(over, active, mouseY);
    setDropPosition(calculatedPosition);
  }, [mouseY, calculateDropPosition, tasks, currentFileId, getSection]);

  // Handle drag end - update task order and/or due date
  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    
    // Capture values before clearing state
    const currentMouseY = mouseY;
    const currentDropPosition = dropPosition;
    const currentDragOverSection = dragOverSection;
    
    // Clear state
    setDragOverTaskId(null);
    setDropPosition(null);
    setMouseY(null);
    setDragOverSection(null);
    setDragOverSectionHeader(null);

    // If no drop target, do nothing
    if (!over || !active) {
      return;
    }

    const draggedTaskClientId = active.id;
    
    // Find the dragged task by clientId - only from current file
    const draggedTask = tasks.find(t => t._reactKey === draggedTaskClientId && t.file_id === currentFileId);
    
    if (!draggedTask) {
      return;
    }

    // Check if dropping on a section (empty section)
    let targetSection = null;
    if (over.id && over.id.startsWith('section-')) {
      targetSection = over.id.replace('section-', '');
    } else {
      const overElement = document.getElementById(over.id);
      if (overElement && overElement.dataset.section) {
        targetSection = overElement.dataset.section;
      }
    }
    
    if (targetSection) {
      const sourceSection = getSection(draggedTask);

      // Dropping on Work in progress section: set status to wip (and optionally reorder)
      if (targetSection === 'work-in-progress') {
        try {
          if (updateTaskStatus) {
            await updateTaskStatus(draggedTask.id, 'wip');
          }
          const targetSectionTasks = tasks.filter(t => {
            if (t.file_id !== currentFileId) return false;
            if (t._reactKey === draggedTaskClientId) return false;
            return getSection(t) === 'work-in-progress';
          });
          if (targetSectionTasks.length > 0) {
            const taskOrders = targetSectionTasks.map(task => ({
              id: task.id,
              prioritize_order: (task.prioritize_order ?? 0) + 1
            }));
            taskOrders.push({ id: draggedTask.id, prioritize_order: 0 });
            await updateTaskOrders(taskOrders, true, { view: 'prioritize', origin: 'drag' });
          }
        } catch (error) {
          console.error('Error updating task:', error);
          fetchTasks(currentFileId);
        }
        return;
      }

      // Dropping on Done section: set status to complete
      if (targetSection === 'done') {
        try {
          if (updateTaskStatus) {
            await updateTaskStatus(draggedTask.id, 'complete');
          }
        } catch (error) {
          console.error('Error updating task:', error);
          fetchTasks(currentFileId);
        }
        return;
      }
      
      // Determine if we need to update due date (only if moving to a different section)
      let newDueDate = draggedTask.due_date;
      let isLaterDate = draggedTask.is_later_date || false;
      
      if (sourceSection !== targetSection) {
        // Moving to a different section - update due date
        if (targetSection === 'no-due-date') {
          newDueDate = null;
          isLaterDate = false;
        } else if (targetSection === 'today') {
          newDueDate = getTodayDate();
          isLaterDate = false;
        } else if (targetSection === 'later') {
          newDueDate = getLaterDate();
          isLaterDate = true;
        }
      }
      
      // Get all tasks in the target section to calculate prioritize_order
      const targetSectionTasks = tasks.filter(t => {
        if (t.file_id !== currentFileId) return false;
        if (t._reactKey === draggedTaskClientId) return false; // Exclude dragged task
        return getSection(t) === targetSection;
      });
      
      // Prepare updates for all tasks in the section
      const taskOrders = [];
      
      if (targetSectionTasks.length > 0) {
        // There are existing tasks - place the dropped task first
        // Set new task to 0 and increment all existing tasks by 1
        targetSectionTasks.forEach(task => {
          const currentOrder = task.prioritize_order !== null && task.prioritize_order !== undefined 
            ? task.prioritize_order 
            : 0; // If no prioritize_order, treat as 0
          taskOrders.push({
            id: task.id,
            prioritize_order: currentOrder + 1
          });
        });
        
        // Add the dragged task update (set to 0 to be first)
        taskOrders.push({
          id: draggedTask.id,
          prioritize_order: 0,
          due_date: newDueDate,
          is_later_date: isLaterDate
        });
      } else {
        // Section is empty - just set the new task to 0
        taskOrders.push({
          id: draggedTask.id,
          prioritize_order: 0,
          due_date: newDueDate,
          is_later_date: isLaterDate
        });
      }
      
      // Update all tasks
      try {
        await updateTaskOrders(taskOrders, true, { view: 'prioritize', origin: 'drag' });
        if (sourceSection === 'work-in-progress' && updateTaskStatus) {
          await updateTaskStatus(draggedTask.id, 'incomplete');
        }
        if (sourceSection === 'done' && updateTaskStatus) {
          await updateTaskStatus(draggedTask.id, 'incomplete');
        }
      } catch (error) {
        console.error('Error updating task:', error);
        fetchTasks(currentFileId);
      }
      return;
    }

    // Dropping on another task
    const dropTargetClientId = over.id;

    // Don't do anything if dropped on itself
    if (draggedTaskClientId === dropTargetClientId) {
      return;
    }

    const dropTargetTask = tasks.find(t => t._reactKey === dropTargetClientId && t.file_id === currentFileId);

    if (!dropTargetTask) {
      return;
    }

    const sourceSection = getSection(draggedTask);
    const dropTargetSection = getSection(dropTargetTask);

    // When dropping on a task in Work in progress from another section, set status to wip first
    if (dropTargetSection === 'work-in-progress' && sourceSection !== 'work-in-progress' && updateTaskStatus) {
      try {
        await updateTaskStatus(draggedTask.id, 'wip');
      } catch (error) {
        console.error('Error updating task status:', error);
        fetchTasks(currentFileId);
        return;
      }
    }

    // When dropping on a task in Done from another section, set status to complete first
    if (dropTargetSection === 'done' && sourceSection !== 'done' && updateTaskStatus) {
      try {
        await updateTaskStatus(draggedTask.id, 'complete');
      } catch (error) {
        console.error('Error updating task status:', error);
        fetchTasks(currentFileId);
        return;
      }
    }

    // Calculate drop position
    const calculatedPosition = currentDropPosition || calculateDropPosition(over, active, currentMouseY);
    const placeBefore = calculatedPosition === 'before';

    // Get all tasks in the target section (same section as drop target)
    const targetSectionTasks = tasks.filter(t => {
      if (t.file_id !== currentFileId) return false;
      if (t._reactKey === draggedTaskClientId) return false; // Exclude dragged task
      return getSection(t) === dropTargetSection;
    });

    // Sort by prioritize_order, then by order as fallback
    targetSectionTasks.sort((a, b) => {
      const orderA = a.prioritize_order !== null && a.prioritize_order !== undefined 
        ? a.prioritize_order 
        : (a.order !== null && a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER);
      const orderB = b.prioritize_order !== null && b.prioritize_order !== undefined 
        ? b.prioritize_order 
        : (b.order !== null && b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER);
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      return (a.created_at || '').localeCompare(b.created_at || '');
    });

    // Find the index of the drop target in the sorted list
    const dropTargetIndex = targetSectionTasks.findIndex(t => t._reactKey === dropTargetClientId);

    if (dropTargetIndex === -1) {
      return;
    }

    // Calculate new prioritize_order and collect all order changes
    const taskOrders = [];
    let newPrioritizeOrder;
    let newDueDate = draggedTask.due_date;
    let newIsLaterDate = draggedTask.is_later_date || false;

    // If moving to a different section, update due date (work-in-progress does not set due date)
    if (sourceSection !== dropTargetSection && dropTargetSection !== 'work-in-progress') {
      if (dropTargetSection === 'no-due-date') {
        newDueDate = null;
        newIsLaterDate = false;
      } else if (dropTargetSection === 'today') {
        newDueDate = getTodayDate();
        newIsLaterDate = false;
      } else if (dropTargetSection === 'later') {
        newDueDate = getLaterDate();
        newIsLaterDate = true;
      }
    }

    if (placeBefore) {
      // Insert before drop target
      const dropTargetOrder = dropTargetTask.prioritize_order !== null && dropTargetTask.prioritize_order !== undefined 
        ? dropTargetTask.prioritize_order 
        : (dropTargetTask.order !== null && dropTargetTask.order !== undefined ? dropTargetTask.order : 0);
      newPrioritizeOrder = dropTargetOrder;
      
      // Collect prioritize_order updates for subsequent tasks
      for (let i = dropTargetIndex; i < targetSectionTasks.length; i++) {
        const sibling = targetSectionTasks[i];
        const siblingOrder = sibling.prioritize_order !== null && sibling.prioritize_order !== undefined 
          ? sibling.prioritize_order 
          : (sibling.order !== null && sibling.order !== undefined ? sibling.order : 0);
        if (siblingOrder >= dropTargetOrder) {
          taskOrders.push({ 
            id: sibling.id, 
            prioritize_order: siblingOrder + 1 
          });
        }
      }
    } else {
      // Insert after drop target
      const nextSibling = targetSectionTasks[dropTargetIndex + 1];
      const dropTargetOrder = dropTargetTask.prioritize_order !== null && dropTargetTask.prioritize_order !== undefined 
        ? dropTargetTask.prioritize_order 
        : (dropTargetTask.order !== null && dropTargetTask.order !== undefined ? dropTargetTask.order : 0);
      
      if (nextSibling) {
        const nextOrder = nextSibling.prioritize_order !== null && nextSibling.prioritize_order !== undefined 
          ? nextSibling.prioritize_order 
          : (nextSibling.order !== null && nextSibling.order !== undefined ? nextSibling.order : Number.MAX_SAFE_INTEGER);
        
        if (nextOrder - dropTargetOrder <= 1) {
          // Need to shift all subsequent tasks
          for (let i = dropTargetIndex + 1; i < targetSectionTasks.length; i++) {
            const sibling = targetSectionTasks[i];
            const siblingOrder = sibling.prioritize_order !== null && sibling.prioritize_order !== undefined 
              ? sibling.prioritize_order 
              : (sibling.order !== null && sibling.order !== undefined ? sibling.order : 0);
            if (siblingOrder !== Number.MAX_SAFE_INTEGER) {
              taskOrders.push({ 
                id: sibling.id, 
                prioritize_order: siblingOrder + 1 
              });
            }
          }
          newPrioritizeOrder = dropTargetOrder + 1;
        } else {
          // There's space between drop target and next sibling
          newPrioritizeOrder = dropTargetOrder + 1;
        }
      } else {
        // Dropping at the end - find max order and add 1
        const maxOrder = targetSectionTasks.length > 0
          ? Math.max(...targetSectionTasks.map(t => {
              const order = t.prioritize_order !== null && t.prioritize_order !== undefined 
                ? t.prioritize_order 
                : (t.order !== null && t.order !== undefined ? t.order : 0);
              return order;
            }))
          : 0;
        newPrioritizeOrder = maxOrder + 1;
      }
    }

    // Add the dragged task update
    taskOrders.push({
      id: draggedTask.id,
      prioritize_order: newPrioritizeOrder,
      due_date: newDueDate,
      is_later_date: newIsLaterDate
    });

    // Batch update all orders
    try {
      await updateTaskOrders(taskOrders, true, { view: 'prioritize', origin: 'drag' });
      if (sourceSection === 'work-in-progress' && dropTargetSection !== 'work-in-progress' && updateTaskStatus) {
        await updateTaskStatus(draggedTask.id, 'incomplete');
      }
      if (sourceSection === 'done' && dropTargetSection !== 'done' && updateTaskStatus) {
        await updateTaskStatus(draggedTask.id, 'incomplete');
      }
    } catch (error) {
      console.error('Error updating task orders:', error);
      fetchTasks(currentFileId);
    }
  }, [tasks, currentFileId, mouseY, dropPosition, dragOverSection, updateTaskOrders, fetchTasks, getTodayDate, getLaterDate, calculateDropPosition, getSection, updateTaskStatus]);

  const handleDragCancel = useCallback(() => {
    setDragOverTaskId(null);
    setDropPosition(null);
    setMouseY(null);
    setDragOverSection(null);
    setDragOverSectionHeader(null);
  }, []);

  return {
    sensors,
    collisionDetection,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    dragOverTaskId,
    dropPosition,
    dragOverSection,
    dragOverSectionHeader
  };
}
